import React from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { paths } from "ui/routes";
import { Spinner } from "ui/components";
import { configpublishsetting } from "core/services/site/project";
import { useSessionContext } from "ui/context/SessionContext";
import { usePublishContext } from "ui/context/PublishContext";
import usePermissions from "ui/hooks/usePermissions";
import * as localStorageKeys from "core/constants/localStorageKeys";
import styles from "./Oauth.module.scss";

const Oauth: React.FC = () => {
    const { platform } = useParams<{ platform: string }>();
    const sessionContext = useSessionContext();
    const publishContext = usePublishContext();
    const navigate = useNavigate();
    const userPermissions = usePermissions();
    const query = new URLSearchParams(useLocation().search);
    const [verified, setVerified] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>();
    const verifiedTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    React.useEffect(() => {
        if (verified) {
            publishContext.reloadSocialAccounts();
            verifiedTimeoutRef.current = setTimeout(() => {
                navigate(paths.settingsSocial);
            }, 1000);
        } else if (verifiedTimeoutRef.current) {
            clearTimeout(verifiedTimeoutRef.current);
        }
    }, [verified, navigate, publishContext]);

    const handleVerify = async (): Promise<void> => {
        if (platform === "twitter") {
            const token = query.get("oauth_token");
            const verifier = query.get("oauth_verifier");
            const twitterAccountKey = localStorage.getItem(
                localStorageKeys.TW_ACC_KEY,
            );
            const twitterVerifyToken = localStorage.getItem(
                localStorageKeys.TW_VERIFY_TOKEN,
            );
            const twitterVerifySecret = localStorage.getItem(
                localStorageKeys.TW_VERIFY_SECRET,
            );

            if (
                !token ||
                !verifier ||
                !twitterAccountKey ||
                !twitterVerifyToken ||
                !twitterVerifySecret
            ) {
                setError("We don't have complete information to verify");
            } else {
                try {
                    if (!sessionContext.session) {
                        throw Error("no session");
                    }
                    if (!sessionContext.selectedProject) {
                        throw Error("no selected project");
                    }

                    const result = await configpublishsetting(
                        sessionContext.session.token,
                        sessionContext.selectedProject.id,
                        twitterAccountKey,
                        "completeauthentication",
                        {
                            token: twitterVerifyToken,
                            tokensecret: twitterVerifySecret,
                            oauthverifier: verifier,
                        },
                    );
                    if (result.error) {
                        throw new Error(result.error);
                    }

                    localStorage.removeItem(localStorageKeys.TW_ACC_KEY);
                    localStorage.removeItem(localStorageKeys.TW_VERIFY_TOKEN);
                    localStorage.removeItem(localStorageKeys.TW_VERIFY_SECRET);
                    setVerified(true);
                } catch (err) {
                    setError(`Oops... ${err}`);
                }
            }
        } else if (platform === "facebook") {
            const code = query.get("code");
            const facebookAccountKey = localStorage.getItem(
                localStorageKeys.FB_ACC_KEY,
            );
            const facebookRedirectUrl = localStorage.getItem(
                localStorageKeys.FB_REDIRECT_URL,
            );

            if (!code || !facebookAccountKey || !facebookRedirectUrl) {
                setError("We don't have complete information to verify");
            } else {
                try {
                    if (!sessionContext.session) {
                        throw Error("no session");
                    }
                    if (!sessionContext.selectedProject) {
                        throw Error("no selected project");
                    }

                    const result = await configpublishsetting(
                        sessionContext.session.token,
                        sessionContext.selectedProject.id,
                        facebookAccountKey,
                        "createaccesstoken",
                        {
                            redirecturl: facebookRedirectUrl,
                            code,
                        },
                    );
                    if (result.error) {
                        throw new Error(result.error);
                    }

                    localStorage.removeItem(localStorageKeys.FB_ACC_KEY);
                    localStorage.removeItem(localStorageKeys.FB_REDIRECT_URL);
                    setVerified(true);
                } catch (err) {
                    setError(`Oops... ${err}`);
                }
            }
        } else if (platform === "youtube") {
            const code = query.get("code");
            const scope = query.get("scope");
            const youtubeAccountKey = localStorage.getItem(
                localStorageKeys.YT_ACC_KEY,
            );
            const youtubeRedirectUrl = localStorage.getItem(
                localStorageKeys.YT_REDIRECT_URL,
            );

            if (!code || !scope || !youtubeAccountKey || !youtubeRedirectUrl) {
                setError("We don't have complete information to verify");
            } else {
                try {
                    if (!sessionContext.session) {
                        throw Error("no session");
                    }
                    if (!sessionContext.selectedProject) {
                        throw Error("no selected project");
                    }

                    const result = await configpublishsetting(
                        sessionContext.session.token,
                        sessionContext.selectedProject.id,
                        youtubeAccountKey,
                        "createaccesstoken",
                        {
                            redirecturl: youtubeRedirectUrl,
                            code,
                        },
                    );
                    if (result.error) {
                        throw new Error(result.error);
                    }

                    localStorage.removeItem(localStorageKeys.YT_ACC_KEY);
                    localStorage.removeItem(localStorageKeys.YT_REDIRECT_URL);
                    setVerified(true);
                } catch (err) {
                    setError(`Oops... ${err}`);
                }
            }
        } else {
            setError("Platform unknown");
        }
    };

    React.useEffect(() => {
        if (platform) {
            if (userPermissions.canConfigureShare) {
                handleVerify();
            } else {
                setError(
                    `You don't have the correct permissions to configure ${platform}`,
                );
            }
        } else {
            setError("Incomplete request, please go back and try again");
        }
    }, [platform, userPermissions.canConfigureShare]);

    return (
        <div className={styles.verifypage}>
            {platform && !verified && !error && (
                <Spinner className={styles.spinner} size="medium" />
            )}
            <div className={styles.msg}>
                {!verified && !error && `Processing ${platform} verification`}
                {verified && "Verified"}
                {error && error}
            </div>
        </div>
    );
};

export default Oauth;
